import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useCookies } from "react-cookie";

import {
  BasketCardFooter,
  BasketCardGrey,
  GreyCard,
  Img,
  PartnerLogo,
  Price,
  PriceAndTag,
  RemoveBtnArea,
  TagAndIcon,
  TermsAndConditionsArea,
} from "./BasketCardStyles";

import BasketCardFooterV2 from "./BasketCardFooterV2";

import CheckMark from "components/IconImage/checkmark";
import { removeOfferFromCart } from "state/cart-slice";
import Offer from "types/Offer";
import generateCurrencyValue from "utils/generateCurrencyValue";
import { ULFEvents } from "types/ULF";
import { LinkButton } from "components/Button";
import { setModal, monetaryConfig, sendULFEvent } from "state/config-slice";
import { AppDispatch } from "state/reducers-combiner";
import FeatureWrapperByPartner from "components/FeatureToggle/FeatureWrapperByPartner";
import { isFeatureActive } from "utils/getFeature";
import { replaceSpacesAndSymbols } from "utils/replaceSpacesAndSymbols";

interface IBasketProps {
  offer: Offer;
}

const BasketCard = ({ offer }: IBasketProps) => {
  const {
    trial,
    name,
    partner,
    marketplaceInfo: { price, tncs, tag, logoName, priceText },
  } = offer || {};

  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const monetarySettings = useSelector(monetaryConfig);
  const [cookies] = useCookies();

  const isStartingPrice = offer?.marketplaceInfo?.isStartingPrice ?? offer?.marketplaceInfo?.agency;

  const removeOfferFromBasket = () => {
    dispatch(removeOfferFromCart(offer));
    dispatch(
      sendULFEvent({
        page: window.location.pathname,
        "event-name": ULFEvents.BASKET_REMOVE,
        "offer-name": name,
        partner,
        source: cookies.SOURCE,
        isTrackingAllowed: cookies.SUBSCRIPTIONPLUS_TRACKING || false,
      }),
    );
  };

  const onTncClickHandle = () => {
    isFeatureActive("T&Cs basket 1 click redirect")
      ? window.open(tncs?.description, "_blank")
      : dispatch(
          setModal({
            showModal: true,
            title: t("{{ partner }} Terms and Conditions", { partner: logoName }),
            children: (
              <div
                data-testid="trialOffer-t&nc-basket-description"
                dangerouslySetInnerHTML={{
                  __html: tncs?.description || "",
                }}
              />
            ),
            textAlign: "left",
            icon: null,
            type: "",
            size: tncs?.size,
            button: null,
          }),
        );
  };

  const isWeekly = offer?.duration?.name === "WEEKLY";
  const idEnricher = replaceSpacesAndSymbols(offer?.marketplaceInfo?.title);

  return (
    <BasketCardGrey data-testid={`basket-card-${idEnricher}`}>
      <GreyCard>
        <PartnerLogo>
          <Img src={offer?.images?.logoLandscape} alt={`${partner}`} data-testid={`basket-card-${idEnricher}-logo`} />
          <PriceAndTag>
            <Price data-testid="basketCardPrice">
              {priceText
                ? t("{{ priceText }}", { priceText })
                : !isStartingPrice
                ? t(isWeekly ? "{{value}}/wk." : "{{value}}/mo.", {
                    value: generateCurrencyValue(price, monetarySettings),
                  })
                : t(isWeekly ? "From {{value}}/wk." : "From {{value}}/mo.", {
                    value: generateCurrencyValue(price, monetarySettings),
                  })}
            </Price>
            {trial ? (
              <TagAndIcon data-testid="basketCardTag">
                <CheckMark />
                {t("{{ tag }}", { tag })}
              </TagAndIcon>
            ) : (
              <></>
            )}
          </PriceAndTag>
        </PartnerLogo>
        {isFeatureActive("Show V2 of basket card footer") ? (
          <BasketCardFooterV2
            offer={offer}
            onTncClickHandle={onTncClickHandle}
            removeOfferFromBasket={removeOfferFromBasket}
          />
        ) : (
          <BasketCardFooter>
            <RemoveBtnArea>
              <LinkButton
                data-testid={`basket-remove-${idEnricher}`}
                title={t("Remove")}
                onClick={removeOfferFromBasket}
              />
            </RemoveBtnArea>
            {!offer.marketplaceInfo.agency && (
              <FeatureWrapperByPartner name="T&Cs basket" partner={partner}>
                <TermsAndConditionsArea onClick={onTncClickHandle} data-testid="offer-t&cs">
                  {t("{{ partner }} T&Cs", { partner: logoName })}
                </TermsAndConditionsArea>
              </FeatureWrapperByPartner>
            )}
          </BasketCardFooter>
        )}
      </GreyCard>
    </BasketCardGrey>
  );
};

export default BasketCard;
